// EvaluationTemplatesPage — 2-pane workspace browser. Left rail lists the
// workspace's eval templates; clicking a row swaps the center pane to that
// template's detail/run view. Mirrors the layout pattern from GraderPage.

import {
  Badge,
  Body1,
  Button,
  Spinner,
  Tab,
  TabList,
  mergeClasses,
} from '@fluentui/react-components';
import { Add24Filled, Add24Regular, bundleIcon } from '@fluentui/react-icons';
import type { ReactElement } from 'react';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMatch, useNavigate, useOutlet, useParams } from 'react-router';

import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useEvaluationTemplates } from '../../hooks/useEvaluationTemplates/useEvaluationTemplates.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';
import type { EvaluationTemplate } from '../../types/evaluationTemplate.js';

import { EvaluationTemplateDetailView } from './EvaluationTemplateDetailView/EvaluationTemplateDetailView.js';
import type { EvaluationTemplateFormOutletContext } from './EvaluationTemplateForm/EvaluationTemplateForm.js';
import { useStyles } from './EvaluationTemplatesPage.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const AddIcon = bundleIcon(Add24Filled, Add24Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

type FilterValue = 'all' | 'active' | 'draft';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface RowProps {
  template: EvaluationTemplate;
  selected: boolean;
  onSelect: (name: string) => void;
}

function TemplateRow({ template, selected, onSelect }: RowProps) {
  const styles = useStyles();

  return (
    <button
      type="button"
      className={mergeClasses(styles.row, selected && styles.rowSelected)}
      onClick={() => onSelect(template.name)}
      aria-pressed={selected}
      aria-label={`Open template ${template.name}`}
    >
      <div className={styles.rowNameLine}>
        <span className={styles.rowName}>{template.name}</span>
      </div>
      <span className={styles.rowMeta}>
        Created {formatDate(template.systemProperties.createdDate)}
      </span>
    </button>
  );
}

export function EvaluationTemplatesPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { workspaceName: workspaceNameParam } = useParams<{
    workspaceName: string;
  }>();
  const { activeName } = useWorkspaceContext();

  // When the user switches workspace via the switcher, navigate to the new
  // workspace's evaluation-templates URL.
  useEffect(() => {
    if (activeName && workspaceNameParam && activeName !== workspaceNameParam) {
      void navigate(
        `/workspaces/${encodeURIComponent(activeName)}/evaluation-templates`,
        { replace: true },
      );
    }
  }, [activeName, workspaceNameParam, navigate]);

  const { activeWorkspace, isLoading: workspacesLoading } = useWorkspaces();
  const workspaceName = activeWorkspace?.name ?? null;

  // Build workspace-scoped base path for navigation.
  const basePath = `/workspaces/${encodeURIComponent(workspaceNameParam!)}/evaluation-templates`;

  // Surfaces a "New eval template" placeholder row in the rail while the
  // create flow is active — same pattern as GraderPage's create mode.
  const isCreating =
    useMatch('/workspaces/:workspaceName/evaluation-templates/new') !== null;
  // When viewing a template via the URL, the URL is the source of truth for
  // the selected row.
  const detailMatch = useMatch(
    '/workspaces/:workspaceName/evaluation-templates/:templateName',
  );
  const routeTemplateName =
    detailMatch?.params.templateName !== undefined &&
    detailMatch.params.templateName !== 'new'
      ? detailMatch.params.templateName
      : null;

  const {
    templates,
    isLoading,
    error,
    refresh,
    upsertTemplate,
    removeTemplate,
  } = useEvaluationTemplates({
    workspaceName,
  });

  const [filter, setFilter] = useState<FilterValue>('all');

  const filteredTemplates = useMemo(() => {
    switch (filter) {
      case 'active':
        return templates.filter((t) => !t.isDraft);
      case 'draft':
        return templates.filter((t) => t.isDraft);
      default:
        return templates;
    }
  }, [templates, filter]);

  // The route param (:templateName) is the single source of truth for the
  // selected template. When the URL points at no / an unknown / a filtered-out
  // template, fall back to the first visible row so the center pane always has
  // something to show; an effect then syncs that choice back into the URL.
  const selectedName = useMemo<string | null>(() => {
    if (
      routeTemplateName !== null &&
      filteredTemplates.some((t) => t.name === routeTemplateName)
    ) {
      return routeTemplateName;
    }
    return filteredTemplates[0]?.name ?? null;
  }, [routeTemplateName, filteredTemplates]);

  // Keep the URL in step with the effective selection. Because the
  // :templateName child route has no component of its own, this navigation
  // re-matches without remounting the page. Skipped while creating (the /new
  // outlet owns the pane) or before the list has loaded.
  useEffect(() => {
    if (isCreating || isLoading) return;
    if (selectedName === routeTemplateName) return;
    const target =
      selectedName === null
        ? basePath
        : `${basePath}/${encodeURIComponent(selectedName)}`;
    void navigate(target, { replace: true });
  }, [
    isCreating,
    isLoading,
    selectedName,
    routeTemplateName,
    basePath,
    navigate,
  ]);

  const listSelectedName = isCreating ? null : selectedName;

  // The left rail highlights `selectedName` immediately on click, but the
  // center detail (a heavy form mount) reads this DEFERRED value so React
  // can commit the rail's focus change first and render the detail in a
  // non-blocking pass.
  const detailSelectedName = useDeferredValue(selectedName);

  // Row click navigates to the template's URL. Because :templateName has no
  // component, this re-matches without remounting the page.
  const handleRowSelect = useCallback(
    (name: string) => {
      void navigate(`${basePath}/${encodeURIComponent(name)}`, {
        replace: true,
      });
    },
    [navigate, basePath],
  );

  // After a template is deleted, drop it from the list immediately and
  // navigate to the base path so the selection falls back to the first
  // remaining template.
  const handleTemplateDeleted = useCallback(
    (name: string) => {
      removeTemplate(name);
      void navigate(basePath, { replace: true });
    },
    [removeTemplate, navigate, basePath],
  );

  const handleTemplateCreated = useCallback(
    (template: EvaluationTemplate) => {
      upsertTemplate(template);
    },
    [upsertTemplate],
  );
  const childOutlet = useOutlet({
    onCreated: handleTemplateCreated,
  } satisfies EvaluationTemplateFormOutletContext) as ReactElement | null;

  const goToCreate = useCallback(() => {
    void navigate(`${basePath}/new`);
  }, [navigate, basePath]);

  // Workspace gating happens before the panes — keeps the layout simple and
  // matches what users see on GraderPage.
  if (workspacesLoading) {
    return (
      <div className={styles.fullPagePlaceholder}>
        <Spinner label="Loading workspaces..." />
      </div>
    );
  }

  if (workspaceName === null) {
    return (
      <div className={styles.fullPagePlaceholder}>
        <Body1>
          Pick a workspace from the top bar to view its eval templates.
        </Body1>
      </div>
    );
  }

  // Left-pane list body. Loading no longer renders here — the rail stays
  // mounted (whatever rows it has) and the center pane carries the loading
  // state. Error and empty-list still surface inline because they're
  // terminal states for the rail itself.
  let listBody: React.ReactNode;
  if (error !== null) {
    listBody = (
      <div className={styles.fullPagePlaceholder}>
        <Body1 className={styles.errorMessage}>{error}</Body1>
        <Button onClick={refresh}>Try again</Button>
      </div>
    );
  } else if (templates.length === 0 && !isCreating && !isLoading) {
    listBody = null;
  } else {
    listBody = (
      <div className={styles.list}>
        {isCreating && (
          // Temporary placeholder row while the /new flow owns the center
          // pane. Mirrors GraderPage's "New Judge" placeholder so the
          // user sees the new item taking shape in the list.
          <div
            className={mergeClasses(styles.row, styles.rowNew)}
            aria-hidden="true"
          >
            <div className={styles.rowNameLine}>
              <span className={styles.rowName}>New eval template</span>
              <Badge size="small" appearance="tint" color="brand">
                New
              </Badge>
            </div>
            <span className={styles.rowMeta}>
              <em>Editing…</em>
            </span>
          </div>
        )}
        {filteredTemplates.map((t) => (
          <TemplateRow
            key={t.name}
            template={t}
            selected={t.name === listSelectedName}
            onSelect={handleRowSelect}
          />
        ))}
      </div>
    );
  }

  // Center-pane content. The /new outlet (create flow) wins; otherwise the
  // pane shows the selected template's detail. Loading the list of templates
  // surfaces here too, not in the rail, so the rail stays stable while the
  // list refetches.
  let centerBody: React.ReactNode;
  if (isCreating) {
    centerBody = childOutlet;
  } else if (isLoading && templates.length === 0) {
    centerBody = (
      <div className={styles.fullPagePlaceholder}>
        <Spinner label="Loading templates..." />
      </div>
    );
  } else if (error === null && !isLoading && templates.length === 0) {
    centerBody = (
      <div className={styles.centerEmpty}>
        <span className={styles.centerEmptyTitle}>
          No eval templates in this workspace yet
        </span>
        <span className={styles.centerEmptySub}>
          Create one to start running evaluations against your agents.
        </span>
        <Button appearance="primary" icon={<AddIcon />} onClick={goToCreate}>
          Create Eval Template
        </Button>
      </div>
    );
  } else if (detailSelectedName === null) {
    centerBody = (
      <div className={styles.centerEmpty}>
        <span className={styles.centerEmptyTitle}>Select a template</span>
        <span className={styles.centerEmptySub}>
          Choose a template from the list, or create a new one to get started.
        </span>
      </div>
    );
  } else {
    centerBody = (
      <EvaluationTemplateDetailView
        workspaceName={workspaceName}
        templateName={detailSelectedName}
        onDeleted={handleTemplateDeleted}
        onChanged={refresh}
      />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.panelsRow}>
        {/* ─── Left pane: list ─────────────────────────────────────────── */}
        <div className={styles.leftPanel}>
          {templates.length > 0 && (
            <div className={styles.filterTabs}>
              <TabList
                size="small"
                selectedValue={filter}
                onTabSelect={(_, d) => setFilter(d.value as FilterValue)}
              >
                <Tab value="all">All</Tab>
                <Tab value="active">Active</Tab>
                <Tab value="draft">Draft</Tab>
              </TabList>
            </div>
          )}

          {listBody}

          <div className={styles.leftFooter}>
            <Button
              appearance="primary"
              icon={<AddIcon />}
              className={styles.fullWidthButton}
              onClick={goToCreate}
            >
              Create Eval Template
            </Button>
          </div>
        </div>

        {/* ─── Center pane: detail ─────────────────────────────────────── */}
        <div className={styles.centerPanel}>{centerBody}</div>
      </div>
    </div>
  );
}
